import React, { useEffect, useRef, useState } from 'react';
import { TextEditorProps, EditableElement, TextStyle, StageTransform } from './types';
import { SPACING } from '../utils/constants';

/**
 * TextEditor component provides an inline text editing experience
 * Shows a textarea overlay positioned at the text element's location
 * Supports save on blur/Enter and cancel on Escape
 */
export const TextEditor: React.FC<TextEditorProps> = ({
  element,
  textStyle,
  onSave,
  onCancel,
  stageTransform,
  screenBounds,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [content, setContent] = useState(element.data?.text || '');
  const [isBlurEnabled, setIsBlurEnabled] = useState(true);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [textareaHeight, setTextareaHeight] = useState<number | null>(null);

  useEffect(() => {
    // Focus the textarea when mounted
    if (textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }

    // Listen for interactions on toolbar to prevent blur
    const handleToolbarMouseDown = (e: Event) => {
      const target = e.target as HTMLElement;
      // Check if interaction is on toolbar or its children
      if (target.closest('[data-text-toolbar]')) {
        // Clear any existing timeout
        if (blurTimeoutRef.current) {
          clearTimeout(blurTimeoutRef.current);
        }
        setIsBlurEnabled(false);
      }
    };

    // Re-enable blur when clicking outside toolbar
    const handleDocumentClick = (e: Event) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-text-toolbar]')) {
        // Clear any existing timeout
        if (blurTimeoutRef.current) {
          clearTimeout(blurTimeoutRef.current);
        }
        // Small delay to allow the click to complete
        blurTimeoutRef.current = setTimeout(() => {
          setIsBlurEnabled(true);
        }, 100);
      }
    };

    document.addEventListener('mousedown', handleToolbarMouseDown, true);
    document.addEventListener('click', handleDocumentClick, true);
    
    return () => {
      document.removeEventListener('mousedown', handleToolbarMouseDown, true);
      document.removeEventListener('click', handleDocumentClick, true);
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  const handleSave = () => {
    // If textarea height was expanded, update element height
    if (textareaHeight !== null && textareaHeight > element.height) {
      // Element height needs to be expanded to accommodate text
      // This will be handled by the onSave callback which should update the element
      // We pass the new height as part of the save operation
      onSave(content, textStyle, textareaHeight);
    } else {
      onSave(content, textStyle);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      // Save on Enter (Shift+Enter adds new line)
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      // Cancel on Escape
      e.preventDefault();
      onCancel();
    }
  };

  const handleBlur = () => {
    // Only save on blur if blur is enabled (not interacting with toolbar)
    if (isBlurEnabled) {
      handleSave();
    }
  };

  // Get background color from element data
  const backgroundColor = element.data?.backgroundColor || element.type === 'sticky' 
    ? (element.data?.backgroundColor || 'rgb(255, 217, 102)')
    : 'white';

  // Determine vertical alignment based on element type
  const getVerticalAlignment = () => {
    if (element.type === 'sticky') return 'top';
    if (element.type === 'shape') return 'middle';
    if (element.type === 'text') return 'top';
    return 'top'; // default
  };

  const verticalAlign = getVerticalAlignment();

  // Calculate position with stage transform
  const scale = stageTransform?.scale || 1;
  const stageX = stageTransform?.x || 0;
  const stageY = stageTransform?.y || 0;
  
  let transformedX: number;
  let transformedY: number;
  let transformedWidth: number;
  let transformedHeight: number;
  let textareaPadding: number;

  // Use screenBounds from Konva if provided (source of truth for position)
  if (screenBounds) {
    const strokeWidth = element.type === 'shape' ? (element.data?.strokeWidth || 2) : 0;
    const strokeOffset = strokeWidth * scale / 2;
    
    transformedX = screenBounds.x + strokeOffset;
    transformedY = screenBounds.y + strokeOffset;
    transformedWidth = screenBounds.width - (strokeOffset * 2);
    transformedHeight = screenBounds.height - (strokeOffset * 2);
    // Use SPACING.STICKY_PADDING for sticky notes to match renderer
    const padding = element.type === 'sticky' 
      ? (element.data?.padding || SPACING.STICKY_PADDING)
      : element.type === 'shape'
      ? (element.data?.padding || 8) // Shapes use 8px padding
      : element.data?.padding || 16;
    textareaPadding = element.type === 'text' ? 0 : padding * scale;
  } else if (element.type === 'text') {
    // Fallback: Simple positioning for text elements - no stroke offset or padding
    transformedX = element.x * scale + stageX;
    transformedY = element.y * scale + stageY;
    transformedWidth = element.width * scale;
    transformedHeight = element.height * scale;
    textareaPadding = 0; // No padding for pure text elements
  } else {
    // Fallback: For shapes and sticky notes
    const strokeWidth = element.type === 'shape' ? (element.data?.strokeWidth || 2) : 0;
    const strokeOffset = strokeWidth * scale / 2;
    
    transformedX = element.x * scale + stageX + strokeOffset;
    transformedY = element.y * scale + stageY + strokeOffset;
    transformedWidth = element.width * scale - (strokeOffset * 2);
    transformedHeight = element.height * scale - (strokeOffset * 2);
    // Use SPACING.STICKY_PADDING for sticky notes to match renderer
    const padding = element.type === 'sticky' 
      ? (element.data?.padding || SPACING.STICKY_PADDING)
      : element.type === 'shape'
      ? (element.data?.padding || 8) // Shapes use 8px padding
      : element.data?.padding || 16;
    textareaPadding = padding * scale;
  }

  const transformedFontSize = textStyle.fontSize * scale;

  // Calculate textarea style based on text style and vertical alignment
  let finalPadding = textareaPadding;
  let finalTop = transformedY;
  
  // Adjust positioning for vertical alignment (only for shapes)
  if (verticalAlign === 'middle' && element.type === 'shape') {
    // For shapes with middle alignment, calculate centered position
    const lineHeightPx = transformedFontSize * (textStyle.lineHeight || 1.2);
    const centerOffset = (transformedHeight - lineHeightPx) / 2;
    finalTop = transformedY + centerOffset;
    finalPadding = 8 * scale; // Match the padding used in shape rendering
  }

  // Calculate dynamic height for textarea
  const getTextareaHeight = (): string => {
    if (textareaHeight !== null) {
      // Use calculated height if text expanded beyond element
      return `${textareaHeight * scale}px`;
    }
    
    if (verticalAlign === 'middle' && element.type === 'shape') {
      // For shapes, start with single line height, will expand if needed
      return `${transformedFontSize * (textStyle.lineHeight || 1.2)}px`;
    }
    
    // Default to element height
    return `${transformedHeight}px`;
  };

  const textareaStyle: React.CSSProperties = {
    position: 'absolute',
    left: `${transformedX}px`,
    top: `${finalTop}px`,
    width: `${transformedWidth}px`,
    height: getTextareaHeight(),
    fontSize: `${transformedFontSize}px`,
    fontFamily: textStyle.fontFamily,
    fontWeight: textStyle.fontStyle.includes('bold') ? 'bold' : 'normal',
    fontStyle: textStyle.fontStyle.includes('italic') ? 'italic' : 'normal',
    color: textStyle.fill,
    textAlign: textStyle.align,
    lineHeight: textStyle.lineHeight,
    letterSpacing: textStyle.letterSpacing ? `${textStyle.letterSpacing * scale}px` : 'normal',
    padding: `${finalPadding}px`,
    border: 'none',
    borderRadius: `${(element.data?.cornerRadius || 0) * scale}px`,
    outline: 'none',
    resize: 'none',
    backgroundColor: 'transparent',
    zIndex: 10000, // Above backdrop (9999) but below toolbar (10001)
    boxShadow: 'none',
    overflow: 'hidden', // Hide scrollbar, let height expand
  };

  // Calculate text height and adjust textarea/element height
  // This must run after all variables are calculated
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Calculate the required height based on content
    const calculateHeight = () => {
      // Reset height to auto to get accurate scrollHeight
      textarea.style.height = 'auto';
      
      const lineHeight = textStyle.lineHeight || 1.2;
      const fontSize = transformedFontSize;
      const minHeight = fontSize * lineHeight;
      
      // Calculate content height
      const scrollHeight = textarea.scrollHeight;
      const contentHeight = Math.max(scrollHeight, minHeight);
      
      // Account for padding
      const paddingTotal = finalPadding * 2;
      const totalHeight = contentHeight + paddingTotal;
      
      // For shapes, if content is multi-line, expand to fit
      // For other elements, use calculated height
      if (element.type === 'shape' && verticalAlign === 'middle') {
        // For shapes, expand if content exceeds single line
        const singleLineHeight = fontSize * lineHeight;
        if (scrollHeight > singleLineHeight + paddingTotal) {
          // Multi-line content - expand textarea
          const expandedHeight = Math.max(totalHeight, transformedHeight);
          setTextareaHeight(expandedHeight / scale); // Store in layer coordinates
          textarea.style.height = `${expandedHeight}px`;
        } else {
          // Single line - use single line height
          setTextareaHeight(null);
          textarea.style.height = `${singleLineHeight + paddingTotal}px`;
        }
      } else {
        // For sticky notes and other elements, expand if needed
        const requiredHeight = totalHeight;
        if (requiredHeight > transformedHeight) {
          // Content exceeds element height - expand
          setTextareaHeight(requiredHeight / scale); // Store in layer coordinates
          textarea.style.height = `${requiredHeight}px`;
        } else {
          setTextareaHeight(null);
          textarea.style.height = `${transformedHeight}px`;
        }
      }
    };

    // Small delay to ensure DOM is ready
    const timeoutId = setTimeout(calculateHeight, 0);
    return () => clearTimeout(timeoutId);
  }, [content, textStyle, transformedFontSize, finalPadding, transformedHeight, element.type, verticalAlign, scale, element.id]);

  return (
    <textarea
      ref={textareaRef}
      value={content}
      onChange={(e) => setContent(e.target.value)}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      style={textareaStyle}
      placeholder="Enter text..."
    />
  );
};

/**
 * TextEditorOverlay wraps the TextEditor in a portal-like container
 * Renders outside the Konva Stage for DOM-based editing
 */
export const TextEditorOverlay: React.FC<{
  element: EditableElement | null;
  textStyle: TextStyle;
  onSave: (content: string, style: TextStyle, newHeight?: number) => void;
  onCancel: () => void;
  stageTransform?: StageTransform;
  /** Optional screen bounds from Konva (source of truth for position) */
  screenBounds?: { x: number; y: number; width: number; height: number } | null;
}> = ({ element, textStyle, onSave, onCancel, stageTransform, screenBounds }) => {
  if (!element) return null;

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 10000, // Above backdrop but below toolbar
      }}
    >
      <div style={{ pointerEvents: 'all' }}>
        <TextEditor
          element={element}
          textStyle={textStyle}
          onSave={onSave}
          onCancel={onCancel}
          stageTransform={stageTransform}
          screenBounds={screenBounds}
        />
      </div>
    </div>
  );
};
